import React from 'react';
import RoutingUtil from '../util/RoutingUtil';

export const AuthenticatedDataContext = React.createContext('authenticatedData');
export const useAuthenticatedData = () => React.useContext(AuthenticatedDataContext);

class AuthenticatedDataExtractor {
  constructor({ session, data, currentSite, currentGroup }) {
    this._data = data;
    this._session = session;
    this._currentSite = currentSite;
    this._currentGroup = currentGroup;
    this._extractedData = this._extractData();
  }

  getData() {
    return this._extractedData;
  }

  getBaseUrl() {
    const { accountId } = this._extractedData;

    let path = "";
    if (this._currentSite?.id) {
      path = `/sites/${this._currentSite.id}`
    } else if (this._currentGroup?.id) {
      path = `/groups/${this._currentGroup.id}`;
    } else if (accountId) {
      // For account-level pages like dashboards, return the account-scoped path
      path = `/dashboards`;
    }

    return path !== "" ? RoutingUtil.outputPathWithAccount(path, accountId) : "/";
  }

  refreshSession(session) {
    this._session = session;
    this._updateExtractedDataFromSession();
  }

  _updateExtractedDataFromSession(extractedData = this._extractedData) {
    if (!this._session) return;

    extractedData.agencyRole = this._session.agencyRole;
    extractedData.accountRole = this._session.accountRole;
    extractedData.accountIssuesFilter = this._session.accountIssuesFilter;

    if (this._session.avatarColor !== undefined || this._session.avatarUrl !== undefined) {
      extractedData.currentUser = {
        ...extractedData.currentUser,
        avatar: {
          color: this._session.avatarColor,
          url: this._session.avatarUrl,
        }
      };
    }
  }

  _extractData() {
    const accountId = this._determineAccountId();
    const accounts = this._data?.accounts?.nodes || [];
    const account = accounts.find(account => account.id === accountId) || { id: accountId };
    const agency = this._data?.accountAgency;
    const customDomain = this._determineCustomDomain(account);
    const currentUser = this._data?.currentUser || {};

    const extractedData = {
      accountId,
      accounts,
      account,
      customDomain,
      usesSsoId: customDomain?.usesSsoId,
      agency,
      agencyId: agency?.id,
      featureFlags: account.featureFlags || [],
      accountRole: null,
      agencyRole: null,
      accountIssuesFilter: null,
      currentUser,
      groupMemberships: currentUser.groupMemberships || [],
      siteMemberships: currentUser.siteMemberships || [],
      currentSite: this._currentSite || null,
      currentSiteId: this._currentSite?.id || null,
      currentGroup: this._currentGroup || null,
      currentGroupId: this._currentGroup?.id || null,

      // TODO: Remove this legacy data
      site: this._currentSite || null,
      siteId: this._currentSite?.id || null,
      group: this._currentGroup || null,
      groupId: this._currentGroup?.id || null,
    };

    // Apply session data after initial extraction
    this._updateExtractedDataFromSession(extractedData);

    return extractedData;
  }

  _determineAccountId() {
    return this._session?.accountId || this._data?.currentShare?.account?.id;
  }

  _determineCustomDomain(account) {
    return account.customDomain || { usesSsoId: false };
  }
}

export default AuthenticatedDataExtractor;
